import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

const faqs = [
    { q: 'How do I create a new audit?', a: 'Navigate to the relevant audit section (Call, Email, Chat, or Lead) from the sidebar, then click the "New" button at the top-right of the page.' },
    { q: 'How do I assign an escalation?', a: 'Open any escalation from the Escalations page, then use the "Assign To" dropdown to pick a team member. They will be notified instantly.' },
    { q: 'How do I export data?', a: 'Go to Settings → Data & Transfers → Export. Choose your module, date range, and format (CSV or Excel), then click Export.' },
    { q: 'How do I change my password?', a: 'Click your profile avatar in the top-right corner, select "Profile", then scroll to the Security section to update your password.' },
    { q: 'How do I switch between modules?', a: 'Use the Module Switcher in the top header bar (shows as the current module name like "QMS"). Click it to see all available modules.' },
    { q: 'How do I contact support?', a: 'You\'re already here! Use the chat below to describe your issue, or email us at support@oblique.io for urgent matters.' },
];

const SupportPanel = ({ isOpen, onClose }) => {
    const [messages, setMessages] = useState([
        { role: 'bot', text: 'Hi there! How can I help you today? You can ask me anything about Oblique or browse the FAQs below.' }
    ]);
    const [input, setInput] = useState('');
    const [expandedFaq, setExpandedFaq] = useState(null);
    const [activeTab, setActiveTab] = useState('chat');
    const messagesEndRef = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const sendMessage = () => {
        if (!input.trim()) return;
        const userMsg = input.trim();
        setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
        setInput('');
        
        // Simulate bot response
        setTimeout(() => {
            setMessages(prev => [...prev, { 
                role: 'bot', 
                text: "Thanks for reaching out! Our support team has been notified and will get back to you shortly. In the meantime, check out the FAQs — your answer might be there!" 
            }]);
        }, 800);
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        style={{
                            position: 'fixed', inset: 0, zIndex: 9998,
                            background: 'rgba(0,0,0,0.15)'
                        }}
                    />
                    {/* Panel */}
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.97 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 350 }}
                        style={{
                            position: 'fixed',
                            bottom: '16px',
                            left: '16px',
                            width: '380px',
                            height: '520px',
                            zIndex: 9999,
                            background: 'var(--bg-surface)',
                            borderRadius: '16px',
                            border: '1px solid var(--border)',
                            boxShadow: '0 24px 64px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden'
                        }}
                    >
                        {/* Header */}
                        <div style={{
                            padding: '16px 20px',
                            borderBottom: '1px solid var(--border)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: 'linear-gradient(135deg, rgba(59,130,246,0.06), rgba(99,102,241,0.06))'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{
                                    width: '32px', height: '32px', borderRadius: '10px',
                                    background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: 'white'
                                }}>
                                    <ObliqueIcon name="support_agent" size={18} />
                                </div>
                                <div>
                                    <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)' }}>
                                        Oblique Support
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#22c55e', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
                                        Online
                                    </div>
                                </div>
                            </div>
                            <button 
                                onClick={onClose}
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: 'var(--text-tertiary)', padding: '4px', borderRadius: '6px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}
                            >
                                <ObliqueIcon name="close" size={18} />
                            </button>
                        </div>

                        {/* Tabs */}
                        <div style={{ 
                            display: 'flex', borderBottom: '1px solid var(--border)', padding: '0 20px',
                            gap: '0'
                        }}>
                            {['chat', 'faqs'].map(tab => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    style={{
                                        flex: 1, padding: '10px 0', fontSize: '12.5px', fontWeight: '600',
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        color: activeTab === tab ? '#3b82f6' : 'var(--text-tertiary)',
                                        borderBottom: activeTab === tab ? '2px solid #3b82f6' : '2px solid transparent',
                                        transition: 'all 0.15s',
                                        textTransform: 'uppercase', letterSpacing: '0.05em'
                                    }}
                                >
                                    {tab === 'chat' ? 'Chat' : 'FAQs'}
                                </button>
                            ))}
                        </div>

                        {/* Content */}
                        {activeTab === 'chat' ? (
                            <>
                                {/* Messages */}
                                <div style={{ 
                                    flex: 1, overflowY: 'auto', padding: '16px 20px',
                                    display: 'flex', flexDirection: 'column', gap: '12px'
                                }} className="custom-scrollbar">
                                    {messages.map((msg, i) => (
                                        <motion.div
                                            key={i}
                                            initial={{ opacity: 0, y: 6 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.2 }}
                                            style={{
                                                display: 'flex',
                                                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
                                            }}
                                        >
                                            <div style={{
                                                maxWidth: '85%',
                                                padding: '10px 14px',
                                                borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                                                background: msg.role === 'user' ? 'linear-gradient(135deg, #3b82f6, #6366f1)' : 'var(--bg-muted)',
                                                color: msg.role === 'user' ? 'white' : 'var(--text-primary)',
                                                fontSize: '13px',
                                                lineHeight: '1.5'
                                            }}>
                                                {msg.text}
                                            </div>
                                        </motion.div>
                                    ))}
                                    <div ref={messagesEndRef} />
                                </div>
                                {/* Input */}
                                <div style={{
                                    padding: '12px 16px',
                                    borderTop: '1px solid var(--border)',
                                    display: 'flex',
                                    gap: '8px',
                                    alignItems: 'center'
                                }}>
                                    <input
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                                        placeholder="Type your message..."
                                        style={{
                                            flex: 1,
                                            padding: '10px 14px',
                                            borderRadius: '10px',
                                            border: '1px solid var(--border)',
                                            background: 'var(--bg-muted)',
                                            color: 'var(--text-primary)',
                                            fontSize: '13px',
                                            outline: 'none',
                                            transition: 'border-color 0.15s'
                                        }}
                                    />
                                    <button
                                        onClick={sendMessage}
                                        style={{
                                            width: '36px', height: '36px', borderRadius: '10px',
                                            background: input.trim() ? 'linear-gradient(135deg, #3b82f6, #6366f1)' : 'var(--bg-muted)',
                                            border: 'none', cursor: input.trim() ? 'pointer' : 'default',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: input.trim() ? 'white' : 'var(--text-tertiary)',
                                            transition: 'all 0.15s',
                                            flexShrink: 0
                                        }}
                                    >
                                        <ObliqueIcon name="send" size={16} />
                                    </button>
                                </div>
                            </>
                        ) : (
                            /* FAQs Tab */
                            <div style={{ 
                                flex: 1, overflowY: 'auto', padding: '12px 16px',
                                display: 'flex', flexDirection: 'column', gap: '4px'
                            }} className="custom-scrollbar">
                                {faqs.map((faq, i) => (
                                    <div key={i} style={{ borderRadius: '10px', overflow: 'hidden' }}>
                                        <button
                                            onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                                            style={{
                                                width: '100%', textAlign: 'left',
                                                padding: '12px 14px',
                                                background: expandedFaq === i ? 'rgba(59,130,246,0.06)' : 'transparent',
                                                border: 'none', cursor: 'pointer',
                                                fontSize: '13px', fontWeight: '600',
                                                color: expandedFaq === i ? '#3b82f6' : 'var(--text-primary)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                gap: '8px', borderRadius: '10px',
                                                transition: 'all 0.15s'
                                            }}
                                        >
                                            <span>{faq.q}</span>
                                            <ObliqueIcon 
                                                name={expandedFaq === i ? 'expand_less' : 'expand_more'} 
                                                size={18} 
                                                style={{ flexShrink: 0, opacity: 0.5 }} 
                                            />
                                        </button>
                                        <AnimatePresence>
                                            {expandedFaq === i && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.2 }}
                                                    style={{ overflow: 'hidden' }}
                                                >
                                                    <div style={{
                                                        padding: '0 14px 12px',
                                                        fontSize: '12.5px',
                                                        color: 'var(--text-secondary)',
                                                        lineHeight: '1.6'
                                                    }}>
                                                        {faq.a}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ))}
                            </div>
                        )}
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default SupportPanel;
